import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Bot,
  GitBranch,
  MessageSquare,
  Settings2,
  Workflow,
  Check,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Card, CardContent } from '@reflex/ui/components/ui/card';
import {
  PageTitle,
  PageDescription,
  PanelTitle,
  SectionLabel,
} from '@reflex/ui/components/layout/Typography';
import { ProviderAccountList } from '@/components/integration/ProviderAccountList';
import { getProviderState, type ProviderEntry } from '@/components/integration/provider-state';
import { OnboardingProgressIndicator } from '@/components/user-onboarding/OnboardingProgressIndicator';
import {
  useUserOnboardingState,
  useUserOnboardingDismiss,
} from '@/hooks/user-onboarding/useUserOnboardingState';
import { useAuth } from '@/hooks/auth/useAuth';
import { useProviderConnect } from '@/hooks/auth/useProviderConnect';
import { api, getAndClearReturnPath } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { ROUTES } from '@/lib/routes';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { cn } from '@reflex/ui/lib/utils';

const FEATURES = [
  {
    icon: Bot,
    title: 'AI Coding Agents',
    description:
      'Describe a task in plain English and launch a coding agent on an isolated cloud sandbox. Watch it work in real time.',
  },
  {
    icon: Workflow,
    title: 'Automated Workflows',
    description:
      'Build multi-step pipelines with a visual automation editor. Chain agents, approval gates, conditional routing, and LLM calls.',
  },
  {
    icon: GitBranch,
    title: 'Deep Integrations',
    description:
      'Connect GitHub, Slack, Linear, and Sentry so agents can automatically review PRs, triage bugs, and update issues.',
  },
  {
    icon: MessageSquare,
    title: 'Multi-Model Chat',
    description:
      'Chat with Claude or Opencode in a unified interface with persistent conversations and file attachments.',
  },
] as const;

export function UserOnboardingPage() {
  const navigate = useNavigate();
  const { providers: configuredProviders, activeMembership } = useAuth();
  const { data: state } = useUserOnboardingState();
  const dismissMutation = useUserOnboardingDismiss();
  const { connect, connectingProvider } = useProviderConnect();
  const routes = useOrgRoutes();

  const providersQuery = useQuery({
    queryKey: queryKeys.userProviders,
    queryFn: api.getUserProviders,
  });

  const linkedAccounts = providersQuery.data ?? [];
  const linkedByProvider = new Map(linkedAccounts.map((p) => [p.provider as string, p] as const));

  const visibleProviders: ProviderEntry[] = Object.entries(configuredProviders).map(
    ([name, { label, requiresStoredToken }]) => ({ name, label, requiresStoredToken }),
  );

  const handleContinue = async () => {
    try {
      await dismissMutation.mutateAsync();
    } catch (error) {
      toast.error('Could not finish onboarding', {
        description: error instanceof Error ? error.message : 'Try again in a moment.',
      });
      return;
    }
    const returnPath = getAndClearReturnPath();
    if (returnPath) {
      navigate(returnPath);
      return;
    }
    if (activeMembership) {
      // New users on a runloop-plugins org get the in-app intro tour at
      // least once, the moment onboarding is dismissed. The tutorial
      // plugin's web surfaces (including this deep-link route) are gated on
      // the same capability, so we only navigate there when the org has it —
      // ungated orgs keep the default landing.
      const caps = activeMembership.organization.capabilities ?? [];
      if (caps.includes('runloop-plugins')) {
        navigate(ROUTES.tutorial('intro'));
        return;
      }
      navigate(`${routes.runs}?new=true`);
      return;
    }
    navigate(ROUTES.root);
  };

  const allComplete = state && !state.hasOutstanding;
  // Treat a provider as "linked" only when it is actually connected (token
  // present, or sign-in-only). Re-auth rows must still surface so users in
  // the GitHub scope-upgrade case can reconnect from /onboard.
  const allLinked = visibleProviders.every(
    (p) => getProviderState(p, linkedByProvider.get(p.name)) === 'connected',
  );
  const showProviderList = visibleProviders.length > 0 && !allLinked;

  return (
    <div
      className="flex h-full flex-col overflow-y-auto"
      tabIndex={0}
      role="region"
      aria-label="Setup"
      data-testid="onboard-page"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-10 p-6 pb-16 md:p-10">
        {/* Hero */}
        <div className="flex flex-col gap-3 pt-4 md:pt-8">
          <PageTitle>Welcome to Reflex</PageTitle>
          <PageDescription className="max-w-xl text-base">
            An AI coding agent orchestration platform. Launch agents, build automated workflows, and
            connect your favorite dev tools, all from one place.
          </PageDescription>
          {state?.hasOutstanding && state.progress.total > 0 && (
            <div
              className="mt-2 flex items-center gap-3 rounded-lg border border-border/60 bg-card/40 px-3.5 py-2.5"
              data-testid="onboard-progress-indicator"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-status-accent-orange/10 text-status-accent-orange">
                <Settings2 className="h-3.5 w-3.5" />
              </div>
              <OnboardingProgressIndicator
                progress={state.progress}
                title="Your setup progress"
                className="flex-1"
              />
            </div>
          )}
        </div>

        {/* Provider account linking */}
        {showProviderList && (
          <div data-testid="provider-list-section">
            <SectionLabel className="mb-3">Connect your accounts</SectionLabel>
            <ProviderAccountList
              providers={visibleProviders}
              linkedAccounts={linkedAccounts}
              onConnect={connect}
              connectingProvider={connectingProvider}
              showBenefits
            />
          </div>
        )}

        {/* Feature cards */}
        <div data-testid="what-you-can-do">
          <SectionLabel className="mb-4">What you can do</SectionLabel>
          <div className="grid gap-4 sm:grid-cols-2">
            {FEATURES.map((feature) => (
              <Card key={feature.title} className="bg-card/50">
                <CardContent className="flex gap-3 p-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <feature.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <PanelTitle as="h3" size="sm">
                      {feature.title}
                    </PanelTitle>
                    <PageDescription className="mt-0.5 text-xs">
                      {feature.description}
                    </PageDescription>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* All complete state */}
        {allComplete && (
          <div
            className={cn(
              'flex flex-col items-center gap-4 rounded-lg border border-status-success/30 bg-status-success/5 p-8 text-center',
            )}
            data-testid="onboard-complete"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-status-success/10">
              <Check className="h-6 w-6 text-status-success" />
            </div>
            <div>
              <PanelTitle as="h2">You're all set!</PanelTitle>
              <PageDescription className="mt-1">
                All required configuration is complete. You can start using the platform.
              </PageDescription>
            </div>
          </div>
        )}

        {/* Action */}
        <div className="flex flex-col items-end gap-3" data-testid="onboard-actions">
          <Button
            size="lg"
            onClick={handleContinue}
            disabled={dismissMutation.isPending}
            data-testid="onboard-continue"
          >
            {allComplete ? 'Get Started' : 'Continue to Sessions'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          {!allComplete && (
            <PageDescription className="text-xs">
              You can complete the remaining steps anytime from{' '}
              <span className="font-medium text-foreground">Settings</span>.
            </PageDescription>
          )}
        </div>
      </div>
    </div>
  );
}
